// src/services/tradeApi.js — team-to-team player swap with accept/reject flow.
import { ref, get, update, push, onValue } from "firebase/database";

export async function proposeTrade(db, leagueCode, trade) {
  // trade: { fromTeam, toTeam, offeredIds[], requestedIds[], message }
  const code = leagueCode.toUpperCase();
  const newRef = push(ref(db, `leagues/${code}/season/trades`));
  await update(ref(db), {
    [`leagues/${code}/season/trades/${newRef.key}`]: {
      ...trade, id: newRef.key,
      status: "pending",  // pending | accepted | rejected | cancelled | countered
      proposedAt: Date.now(),
    },
  });
  return newRef.key;
}

export async function respondTrade(db, leagueCode, tradeId, action) {
  // action: "accepted" | "rejected" | "cancelled"
  const code = leagueCode.toUpperCase();
  if (action !== "accepted") {
    await update(ref(db), {
      [`leagues/${code}/season/trades/${tradeId}/status`]: action,
      [`leagues/${code}/season/trades/${tradeId}/respondedAt`]: Date.now(),
    });
    return;
  }

  // Execute the swap
  const [leagueSnap, roomSnap] = await Promise.all([
    get(ref(db, `leagues/${code}`)),
    get(ref(db, `rooms/${code}`)),
  ]);
  const league = leagueSnap.val();
  const room   = roomSnap.val();
  if (!league || !room) throw new Error("League/room not found");

  const trade = league?.season?.trades?.[tradeId];
  if (!trade || trade.status !== "pending") throw new Error("Trade no longer valid");

  const teams = [...(room.teams || [])];
  const fromIdx = teams.findIndex(t => t.name === trade.fromTeam);
  const toIdx   = teams.findIndex(t => t.name === trade.toTeam);
  if (fromIdx === -1 || toIdx === -1) throw new Error("Teams not found");

  const fromSquad = teams[fromIdx].squadIds.map(String);
  const toSquad   = teams[toIdx].squadIds.map(String);

  const offeredIds   = trade.offeredIds.map(String);
  const requestedIds = trade.requestedIds.map(String);

  // Validate all offered players still on fromTeam
  for (const id of offeredIds) {
    if (!fromSquad.includes(id)) throw new Error(`Offered player ${id} no longer on ${trade.fromTeam}`);
  }
  for (const id of requestedIds) {
    if (!toSquad.includes(id)) throw new Error(`Requested player ${id} no longer on ${trade.toTeam}`);
  }

  // Perform swap
  const newFromSquad = [...fromSquad.filter(id => !offeredIds.includes(id)), ...requestedIds];
  const newToSquad   = [...toSquad.filter(id => !requestedIds.includes(id)), ...offeredIds];

  const dbUpdates = {
    [`rooms/${code}/teams/${fromIdx}/squadIds`]: newFromSquad.map(Number),
    [`rooms/${code}/teams/${toIdx}/squadIds`]:   newToSquad.map(Number),
    [`leagues/${code}/season/trades/${tradeId}/status`]:      "accepted",
    [`leagues/${code}/season/trades/${tradeId}/respondedAt`]: Date.now(),
  };

  // Log activity
  const actRef = push(ref(db, `leagues/${code}/activity`));
  dbUpdates[`leagues/${code}/activity/${actRef.key}`] = {
    type: "trade",
    text: `Trade completed: ${trade.fromTeam} and ${trade.toTeam} swapped players`,
    at: Date.now(),
  };

  await update(ref(db), dbUpdates);
}

export function subscribeTrades(db, leagueCode, callback) {
  const code = leagueCode.toUpperCase();
  return onValue(ref(db, `leagues/${code}/season/trades`), snap => callback(snap.val() || {}));
}
